/**
 * 未归属请求 — the requests that never resolved a SKU.
 *
 * ── What this row actually is ──
 *
 * Production, 24h, is four unrelated things sharing one bucket:
 *
 *   /v1/messages/count_tokens  completed 200   2,698   free, local estimate
 *   /v1/chat/completions       failed    402     758   balance refusals
 *   /v1/responses              failed    404     289   model not available
 *   /v1/responses             started   NULL      52   still in flight
 *
 * It used to be a row in the ladder table, ranked and graded like a product.
 * It is not a product: the bulk of it is `count_tokens`, which this repo
 * answers from a local estimate and never forwards or bills, and it has no
 * routing targets at all, so it was a permanently ladder-less row in a table
 * about ladders. It is also not a fault, so grading it 严重 sent operators
 * after something that was working exactly as designed.
 *
 * ── What this panel may and may not claim ──
 *
 * The two failure groups inside it ARE findings worth acting on — paying
 * customers turned away for balance, and customers asking for a model we do
 * not serve on that ingress — and they are now nameable: `refusedByCode`
 * covers the `sku_id IS NULL` row, so the 402/404 split is on the wire and is
 * rendered by `RefusalBreakdown`.
 *
 * What is STILL unavailable is the route split. The traffic overview has no
 * route group, so the `count_tokens` share and the in-flight share cannot be
 * separated from each other or from anything else in the non-failing
 * remainder. So: state the counts the model carries, render the refusal codes
 * it now has, and say plainly that the remainder cannot be split — naming
 * which KINDS of request land there without attaching a number to any of
 * them. Presenting the whole bucket as refusals would be the same class of
 * lie as printing 0% for an unsettled margin: a real number under the wrong
 * heading.
 *
 * Deliberately absent: a health grade. There is no subject here to be healthy
 * or broken — a free local estimate and a balance refusal have no shared
 * verdict — and the panel says so rather than leaving a blank where operators
 * are used to seeing a chip.
 */

import { useT } from '../../i18n/index.ts';
import { Panel } from '../../ui/index.ts';
import { formatInteger, formatMaybeInteger, formatRate } from './display.ts';
import { RefusalBreakdown } from './RefusalBreakdown.tsx';
import type { SkuNode } from './types.ts';

interface Props {
  readonly node: SkuNode;
}

export function UnattributedPanel({ node }: Props): JSX.Element {
  const t = useT();
  const traffic = node.traffic;
  // The requests that did not end in a tenant-final failure. NOT "successes":
  // an in-flight request is neither, and the wire's `error_count` predicate
  // leaves it here. The label says 其余 / "the rest" for exactly that reason.
  const remainder = Math.max(0, traffic.requests - traffic.errors);

  return (
    <Panel
      caption={t('health_perspective.unattributed.title')}
      padded
      testId="health-perspective-unattributed"
      className="min-w-0"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="font-mono text-stat text-paper">{formatInteger(traffic.requests)}</span>
          <span className="text-dense text-paper-low">
            {t('health_perspective.unattributed.share', { value: formatRate(traffic.observedShare) })}
          </span>
        </div>

        {/* Only the parts the model can actually name. */}
        <dl className="m-0 flex flex-wrap gap-x-6 gap-y-2">
          <Figure
            label={t('health_perspective.unattributed.refused')}
            value={formatMaybeInteger(traffic.refusedBeforeUpstream)}
            testId="health-perspective-unattributed-refused"
          />
          <Figure
            label={t('health_perspective.unattributed.rate_limited')}
            value={formatInteger(traffic.rateLimited)}
          />
          <Figure
            label={t('health_perspective.unattributed.server_errors')}
            value={formatInteger(traffic.serverErrors)}
          />
          <Figure
            label={t('health_perspective.unattributed.remainder')}
            value={formatInteger(remainder)}
            testId="health-perspective-unattributed-remainder"
          />
        </dl>

        {/* What the refusals were, split by code and by which side of the
            line each falls on. Two of these are findings: paying customers
            turned away, and requests we could not route. */}
        <div className="flex flex-col gap-1.5">
          <h3 className="m-0 text-label text-paper-low">
            {t('health_perspective.refusal.title')}
          </h3>
          <RefusalBreakdown
            groups={traffic.refusedByCode}
            testId="health-perspective-unattributed-codes"
          />
          <p className="m-0 text-micro text-paper-low">{t('health_perspective.refusal.rule')}</p>
        </div>

        {/* The load-bearing sentence: the remainder is not refusals, and this
            read cannot break it down. */}
        <p
          className="m-0 text-micro text-paper-low"
          data-testid="health-perspective-unattributed-composition"
        >
          {t('health_perspective.unattributed.composition_unavailable')}
        </p>
        <p className="m-0 text-micro text-paper-low">
          {t('health_perspective.unattributed.not_graded')}
        </p>
      </div>
    </Panel>
  );
}

function Figure({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
      <dt className="m-0 text-label text-paper-low">{label}</dt>
      <dd className="m-0 font-mono text-body text-paper">{value}</dd>
    </div>
  );
}
